import { useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import RichTextPreview from '../../richTextPreview';
import Info from './info';
import Content from './content';
import { isDateInFuture } from '../../../../utility/dateUtils';
import { onScrollIntoView } from '../../../../utility/uiUtils';

const useStyles = makeStyles(({ palette: { primary } }) => ({
  main: {
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish6}`,
    borderRadius: 4,
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) 230px`,
    width: '100%',
    transition: 'border .2s ease',
  },
  clickableCard: {
    cursor: 'pointer',
    '&:hover': {
      border: `1px solid ${primary.blue1}`,
    },
  },
  disabled: {
    cursor: 'default',
  },
}));

const OneOnOneRecord = ({
  className = null,
  translations,
  record,
  notifications = [],
  hasNotifications = false,
  isNotificationUpdate = false,
  isDisabled = false,
  canEdit = false,
  canDelete = false,
  canAccessUserProfileCheck,
  onEdit = () => {},
  onDelete = () => {},
  onManageTasks = () => {},
  onCardClickHandler = () => {},
  onOpenCustomTopic = () => {},
  onReschedule,
  onAddImpression = () => {},
  goToUserProfilePage,
  onClearNotifications,
}) => {
  const recordRef = useRef(null);
  const classes = useStyles();
  const { meeting_time, canAccessOneOnOne } = record;
  const isFuture = isDateInFuture(meeting_time);

  useEffect(() => {
    if (recordRef && hasNotifications) {
      onScrollIntoView(recordRef, { block: 'center' });
    }
  }, [hasNotifications]);

  const handleCardClick = useCallback(() => {
    if (!canAccessOneOnOne) return;
    return onCardClickHandler(record);
  }, [record, canAccessOneOnOne, onCardClickHandler]);

  const renderRichTextPreview = text => <RichTextPreview text={text} />;

  return (
    <div
      ref={recordRef}
      className={classNames(
        classes.main,
        {
          [classes.clickableCard]: record.canAccessOneOnOne,
          [classes.disabled]: !record.canAccessOneOnOne,
        },
        className
      )}
      onClick={handleCardClick}
    >
      <Content
        translations={translations}
        record={record}
        isFuture={isFuture}
        onOpenCustomTopic={onOpenCustomTopic}
      />
      <Info
        translations={translations}
        canEdit={canEdit}
        canDelete={canDelete}
        record={record}
        canAccessOneOnOne={canAccessOneOnOne}
        isDisabled={isDisabled}
        isFuture={isFuture}
        notifications={notifications}
        hasNotifications={hasNotifications}
        isNotificationUpdate={isNotificationUpdate}
        hasRescheduleButton={canEdit && !isFuture}
        noteLabelComponent={renderRichTextPreview}
        canAccessUserProfileCheck={canAccessUserProfileCheck}
        goToUserProfilePage={goToUserProfilePage}
        onReschedule={onReschedule}
        onAddImpression={onAddImpression}
        onEdit={onEdit}
        onDelete={onDelete}
        onManageTasks={onManageTasks}
        onClearNotifications={onClearNotifications}
      />
    </div>
  );
};

OneOnOneRecord.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.shape({}).isRequired,
  record: PropTypes.shape({}).isRequired,
  notifications: PropTypes.arrayOf(PropTypes.string),
  isNotificationUpdate: PropTypes.bool,
  hasNotifications: PropTypes.bool,
  isDisabled: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onOpenCustomTopic: PropTypes.func,
  onAddImpression: PropTypes.func,
  onCardClickHandler: PropTypes.func,
  onManageTasks: PropTypes.func,
  goToUserProfilePage: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  onClearNotifications: PropTypes.func.isRequired,
};

export default OneOnOneRecord;
